import React from 'react';
import type { PressableProps, View } from 'react-native';
import { ActivityIndicator, Pressable } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { TxKeyPath } from '@/core/i18n';

import { Text } from './text';

type ButtonVariants = VariantProps<typeof button>;
interface Props extends ButtonVariants, Omit<PressableProps, 'disabled'> {
  tx?: TxKeyPath;
  text?: string;
  type:
    | 'primary'
    | 'secondary'
    | 'ghost'
    | 'link'
    | 'primarySmall'
    | 'primaryExtraSmall'
    | 'secondarySmall'
    | 'secondaryExtraSmall';
  onPress: () => void;
  label?: string;
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  disabled?: boolean;
  textStyle?: any;
  style?: any;
  values?: any;
}

export const Button = React.forwardRef<View, Props>(
  (
    {
      tx,
      text,
      label: labelText,
      loading = false,
      variant = 'default',
      disabled = false,
      size = 'default',
      type,
      leftIcon,
      rightIcon,
      onPress,
      textStyle,
      style,
      values,
      ...props
    },
    ref
  ) => {
    const { styles } = useStyles(stylesheet);
    const [pressed, setPressed] = React.useState(false);
    const [hover, setHover] = React.useState(false);

    const getButtonStyle = () => {
      const showDisabled = disabled || loading;
      let stateStyle = styles[`${type}Default`];

      if (type) {
        if (showDisabled) {
          stateStyle = styles[`${type}Disabled`];
        }
        if (pressed) {
          stateStyle = styles[`${type}Pressed`];
        }
        if (hover) {
          stateStyle = styles[`${type}Hover`];
        }
      }

      return [styles.button, stateStyle];
    };

    const getTextStyle = () => {
      const showDisabled = disabled || loading;
      let stateStyle = styles[`${type}TextDefault`];

      if (type) {
        if (showDisabled) {
          stateStyle = styles[`${type}TextDisabled`];
        }
        if (pressed) {
          stateStyle = styles[`${type}TextPressed`];
        }
        if (hover) {
          stateStyle = styles[`${type}TextHover`];
        }
      }

      return [styles.text, stateStyle];
    };

    return (
      <Pressable
        disabled={disabled || loading}
        {...props}
        ref={ref}
        hitSlop={8}
        onPressIn={() => setPressed(true)}
        onPressOut={() => setPressed(false)}
        onPress={onPress}
        style={[getButtonStyle(), style, { opacity: pressed ? 0.5 : 1 }]}
        onHoverIn={() => setHover(true)}
        onHoverOut={() => setHover(false)}
      >
        {props.children ? (
          props.children
        ) : (
          <>
            {loading ? (
              <ActivityIndicator size="small" />
            ) : (
              <>
                {leftIcon}
                {tx ? (
                  <Text
                    style={[getTextStyle(), textStyle]}
                    tx={tx}
                    values={values}
                  />
                ) : null}
                {text ? (
                  <Text style={[getTextStyle(), textStyle]} values={values}>
                    {text}
                  </Text>
                ) : null}

                {rightIcon}
              </>
            )}
          </>
        )}
      </Pressable>
    );
  }
);

const stylesheet = createStyleSheet((theme) => ({
  button: {
    width: '100%',
    height: 50,
    paddingTop: 14,
    paddingRight: 24,
    paddingBottom: 14,
    paddingLeft: 24,
    borderRadius: 8,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  text: {
    fontFamily: 'DMSans-Regular',
    fontSize: 17,
    fontWeight: 500,
    lineHeight: 22,
    letterSpacing: -0.43,
  },
  primaryDefault: {
    backgroundColor: theme.colors.button.primary.default.fill,
  },
  primaryTextDefault: {
    color: theme.colors.button.primary.default.text,
  },
  primaryHover: {
    backgroundColor: theme.colors.button.primary.hover.fill,
  },
  primaryTextHover: {
    color: theme.colors.button.primary.hover.text,
  },
  primaryPressed: {
    backgroundColor: theme.colors.button.primary.pressed.fill,
  },
  primaryTextPressed: {
    color: theme.colors.button.primary.pressed.text,
  },
  primaryDisabled: {
    backgroundColor: theme.colors.button.primary.disabled.fill,
  },
  primaryTextDisabled: {
    color: theme.colors.button.primary.disabled.text,
  },
  secondaryDefault: {
    backgroundColor: theme.colors.button.secondary.default.fill,
    borderWidth: 1,
    borderColor: theme.colors.button.secondary.default.border,
  },
  secondaryTextDefault: {
    color: theme.colors.button.secondary.default.text,
  },
  secondaryHover: {
    backgroundColor: theme.colors.button.secondary.hover.fill,
    borderWidth: 1,
    borderColor: theme.colors.button.secondary.hover.border,
  },
  secondaryTextHover: {
    color: theme.colors.button.secondary.hover.text,
  },
  secondaryPressed: {
    backgroundColor: theme.colors.button.secondary.pressed.fill,
    borderWidth: 1,
    borderColor: theme.colors.button.secondary.pressed.border,
  },
  secondaryTextPressed: {
    color: theme.colors.button.secondary.pressed.text,
  },
  secondaryDisabled: {
    backgroundColor: theme.colors.button.secondary.disabled.fill,
    borderWidth: 1,
    borderColor: theme.colors.button.secondary.disabled.border,
  },
  secondaryTextDisabled: {
    color: theme.colors.button.secondary.disabled.text,
  },
  ghostTextDefault: {
    color: theme.colors.button.ghost.default.text,
  },
  ghostTextHover: {
    color: theme.colors.button.ghost.hover.text,
  },
  ghostTextPressed: {
    color: theme.colors.button.ghost.pressed.text,
  },
  ghostTextDisabled: {
    color: theme.colors.button.ghost.disabled.text,
  },
  linkTextDefault: {
    color: theme.colors.button.link.default.text,
  },
  linkTextHover: {
    color: theme.colors.button.link.hover.text,
  },
  linkTextPressed: {
    color: theme.colors.button.link.pressed.text,
  },
  linkTextDisabled: {
    color: theme.colors.button.link.disabled.text,
  },
  primarySmallDefault: {
    backgroundColor: theme.colors.button.primary.default.fill,
    borderRadius: 100,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
    height: 40,
  },
  primarySmallTextDefault: {
    color: theme.colors.button.primary.default.text,
  },
  primarySmallHover: {
    backgroundColor: theme.colors.button.primary.hover.fill,
    borderRadius: 100,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
    height: 40,
  },
  primarySmallTextHover: {
    color: theme.colors.button.primary.hover.text,
  },
  primarySmallPressed: {
    backgroundColor: theme.colors.button.primary.default.fill,
    borderRadius: 100,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
    height: 40,
  },
  primarySmallTextPressed: {
    color: theme.colors.button.primary.pressed.text,
  },
  primarySmallDisabled: {
    backgroundColor: theme.colors.button.primary.disabled.fill,
    borderRadius: 100,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
    height: 40,
  },
  primarySmallTextDisabled: {
    color: theme.colors.button.primary.disabled.text,
  },
  primaryExtraSmallDefault: {
    backgroundColor: theme.colors.button.primary.default.fill,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: theme.colors.button.primary.default.fill,
    width: 'auto',
    height: 'auto',
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
  },
  primaryExtraSmallTextDefault: {
    color: theme.colors.button.primary.default.text,
    fontSize: 12,
  },
  primaryExtraSmallHover: {
    backgroundColor: theme.colors.button.primary.hover.fill,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: theme.colors.button.primary.hover.fill,
    width: 'auto',
    height: 'auto',
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
  },
  primaryExtraSmallTextHover: {
    color: theme.colors.button.primary.hover.text,
    fontSize: 12,
  },
  primaryExtraSmallPressed: {
    backgroundColor: theme.colors.button.primary.default.fill,
    borderRadius: 100,
    width: 'auto',
    height: 'auto',
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
  },
  primaryExtraSmallTextPressed: {
    color: theme.colors.button.primary.default.text,
    fontSize: 12,
  },
  primaryExtraSmallDisabled: {
    backgroundColor: theme.colors.button.primary.disabled.fill,
    borderRadius: 100,
    width: 'auto',
    height: 'auto',
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
  },
  primaryExtraSmallTextDisabled: {
    color: theme.colors.button.primary.disabled.text,
    fontSize: 12,
  },
  secondarySmallDefault: {
    backgroundColor: theme.colors.button.secondary.default.fill,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: theme.colors.button.secondary.default.border,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
    height: 40,
  },
  secondarySmallTextDefault: {
    color: theme.colors.button.secondary.default.text,
  },
  secondarySmallHover: {
    backgroundColor: theme.colors.button.secondary.default.fill,
    borderWidth: 1,
    borderColor: theme.colors.button.secondary.default.border,
    borderRadius: 100,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
    height: 40,
  },
  secondarySmallTextHover: {
    color: theme.colors.button.secondary.default.text,
  },
  secondarySmallPressed: {
    backgroundColor: theme.colors.button.secondary.default.fill,
    borderRadius: 100,
    borderWidth: 1,
    borderColor: theme.colors.button.secondary.default.border,
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
    height: 40,
  },
  secondarySmallTextPressed: {
    color: theme.colors.button.secondary.default.text,
  },
  secondarySmallDisabled: {},
  secondaryExtraSmallDefault: {
    backgroundColor: theme.colors.button.secondary.default.fill,
    borderRadius: 100,
    width: 'auto',
    height: 'auto',
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
    borderWidth: 1,
    borderColor: theme.colors.button.secondary.default.border,
  },
  secondaryExtraSmallTextDefault: {
    color: theme.colors.button.secondary.default.text,
    fontSize: 12,
  },
  secondaryExtraSmallHover: {
    backgroundColor: theme.colors.button.secondary.default.fill,
    borderRadius: 100,
    width: 'auto',
    height: 'auto',
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
    borderWidth: 1,
    borderColor: theme.colors.button.secondary.default.border,
  },
  secondaryExtraSmallTextHover: {
    color: theme.colors.button.secondary.default.text,
    fontSize: 12,
  },
  secondaryExtraSmallPressed: {
    backgroundColor: theme.colors.button.secondary.default.fill,
    borderRadius: 100,
    width: 'auto',
    height: 'auto',
    paddingLeft: 16,
    paddingRight: 16,
    paddingTop: 8,
    paddingBottom: 8,
    borderWidth: 1,
    borderColor: theme.colors.button.secondary.default.border,
  },
  secondaryExtraSmallTextPressed: {
    color: theme.colors.button.secondary.default.text,
    fontSize: 12,
  },
  secondaryExtraSmallDisabled: {},
}));
